import { parse } from "papaparse";
import { useState } from "react";
import { useDataProvider, useNotify, useRefresh, useResourceContext } from "react-admin";

import { FileRejection } from "react-dropzone";

import { isErrorWithMessage } from "common";

import { ImportDialogState } from "../shared/types";

export const useCsvResourceImportState = <TResource, TCreateEntity>(
  handleClose: () => void,
  handleAssertInput: (input: unknown) => asserts input is TResource[],
  handleMapResourceToCreateEntity: (resource: TResource[]) => TCreateEntity[]
) => {
  const dataProvider = useDataProvider();
  const resource = useResourceContext();
  const refresh = useRefresh();
  const notify = useNotify();

  const [dialogState, setDialogState] = useState<ImportDialogState<TResource>>({
    step: 0,
    file: null,
    message: null
  });

  const onFileAdded = (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
    const rejectedFile = rejectedFiles[0];

    if (rejectedFile) {
      setDialogState({
        step: 0,
        file: null,
        message: rejectedFile.errors[0].message
      });

      return;
    }

    const file = acceptedFiles[0];

    if (file) {
      setDialogState({ step: 0, message: null, file });
    }
  };

  const onFileParseError = (e?: Error) => {
    setDialogState({
      step: -1
    });
    notify(isErrorWithMessage(e) ? e.message : "Invalid CSV file type.", {
      type: "error"
    });
  };

  const onFileConfirm = () => {
    if (dialogState.step === 0 && dialogState.file) {
      setDialogState({
        file: dialogState.file,
        step: 1,
        importedResources: undefined
      });

      parse<TResource>(dialogState.file, {
        complete: (result) => {
          try {
            handleAssertInput(result.data);

            if (dialogState.file) {
              setDialogState({
                file: dialogState.file,
                step: 1,
                importedResources: result.data
              });
            } else {
              setDialogState({
                step: -1
              });
            }
          } catch (e) {
            onFileParseError(e as Error);
          }
        },
        error: onFileParseError,
        transformHeader: (header) => header.toLowerCase(),
        header: true,
        dynamicTyping: true,
        skipEmptyLines: true
      });
    }
  };

  const onSave = async () => {
    if (dialogState.step === 1 && dialogState.importedResources) {
      try {
        setDialogState({ step: 2, status: undefined });

        await dataProvider.createMany(resource, {
          data: handleMapResourceToCreateEntity(dialogState.importedResources)
        });

        setDialogState({ step: 2, status: { status: "success" } });
        refresh();
      } catch (e) {
        if (isErrorWithMessage(e)) {
          setDialogState({ step: 2, status: { status: "error", message: e.message } });
        }
      }
    }
  };

  const onClose = () => {
    handleClose();
    setDialogState({ step: 0, file: null, message: null });
  };

  return {
    dialogState,
    onFileAdded,
    onSave,
    onClose,
    onFileConfirm
  };
};
